import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  HStack,
  Text,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  VStack,
  MenuDivider
} from "@chakra-ui/react";
import { SignedIn, useUser } from "@clerk/clerk-react";
import { useClerk } from "@clerk/clerk-react";
import { motion } from "framer-motion";
import { CgProfile } from "react-icons/cg";
import { Link, useNavigate } from "react-router-dom";

import { MenuLink, TopMenuLinks } from "./navLinks";
import { ThemeColors } from "../styles/theme";

interface NavLinkProps {
  item: MenuLink;
}

const NavLink = ({ item }: NavLinkProps) => {
  return (
    <Link to={item.path} target={item.target}>
      <Box
        px={2}
        py={1}
        rounded={"md"}
        _hover={{
          textDecoration: "none",
          bg: "gray.200"
        }}
      >
        {item.label}
      </Box>
    </Link>
  );
};

interface NavBarProps {
  menuIsVisible: boolean;
  openMenu: () => void;
}

export default function NavBar({ menuIsVisible, openMenu }: NavBarProps) {
  const clerk = useClerk();
  const user = useUser();
  const navigate = useNavigate();

  const menuVariants = {
    open: { rotate: 90 },
    closed: { rotate: 0 }
  };

  async function handleSignOut() {
    localStorage.removeItem("conversationFormValues");
    await clerk.signOut(() => {
      location.href = "/signin";
    });
  }

  return (
    <Box px={4} w={"100%"} bg={"white"} borderBottomColor={"gray.200"} borderBottomWidth={1}>
      <Flex h={16} alignItems={"center"} justifyContent={"space-between"}>
        <Box w={"32px"} h={2} hideFrom={"md"}></Box>
        <Box position="absolute" display={{ md: "none" }} zIndex={1000} left={2}>
          <motion.div
            animate={menuIsVisible ? "open" : "closed"}
            variants={menuVariants}
            transition={{ duration: 0.25 }}
          >
            <IconButton
              aria-label={"Open menu"}
              icon={
                menuIsVisible ? (
                  <CloseIcon color={"white"} bg={ThemeColors.brand.primary} fontSize={"md"} />
                ) : (
                  <HamburgerIcon bg={"transparent"} />
                )
              }
              onClick={() => openMenu()}
              fontSize={"22px"}
              variant={""}
            />
          </motion.div>
        </Box>
        <Link to="/">
          {/* add link to svg here */}

          <HStack spacing={3} alignItems={"left"}>
            <Box bg="white">
              <img src="/images/wwa-logo.webp" alt="Logo" style={{ height: "50px", width: "55px" }} />
            </Box>
            <VStack gap={0} align={"flex-start"} hideBelow={"md"} pl={3}>
              <Text className="logo-text" position={"relative"} left={"0px"} top={"10px"}>
                WWA
              </Text>
              <Text className="logo-text" position={"relative"} left={"0px"} top={"12px"}>
                Admin
              </Text>
            </VStack>
          </HStack>
        </Link>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: clerk.loaded ? 1 : 0 }} transition={{ duration: 0.5 }}>
          <HStack>
            <HStack as={"nav"} spacing={4} display={{ base: "none", md: "flex" }} justify={"flex-end"} pr={4}>
              {TopMenuLinks.map((link) => (
                <NavLink key={link.label} item={link} />
              ))}
            </HStack>

            <SignedIn>
              <Flex alignItems={"center"}>
                <Menu>
                  <MenuButton
                    as={Button}
                    rounded={"full"}
                    variant={"link"}
                    cursor={"pointer"}
                    minW={0}
                    aria-label="User Menu"
                    fontWeight={"400"}
                    _hover={{ textDecoration: "none" }}
                  >
                    <HStack>
                      <Text hideBelow={"md"} color="brand.primary">
                        {user?.user?.firstName}
                      </Text>

                      <Box color="brand.primary" fontSize={"2rem"}>
                        <CgProfile />
                      </Box>
                    </HStack>
                  </MenuButton>
                  <MenuList minW={"0"} borderColor={"gray.300"}>
                    <MenuItem onClick={() => navigate("/account")} textAlign={"center"} px={6}>
                      Account
                    </MenuItem>
                    <MenuItem onClick={() => navigate("/admin")} textAlign={"center"} px={6}>
                      Admin
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem onClick={handleSignOut} px={6}>
                      Sign out
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            </SignedIn>
          </HStack>
        </motion.div>
      </Flex>
    </Box>
  );
}
